import type { Hint } from "@scoach/types";

/**
 * Groups live hints by topic and drops near-duplicates, so the coach column
 * doesn't repeat the same advice when the conversation lingers on one subject.
 *
 * Rules:
 * - Near-duplicate (≥ DUP_THRESHOLD similar on title+summary): dropped.
 * - Same topic (same category + similar title) but new info: added to the SAME
 *   group → rendered as one card with left/right navigation + a count.
 * - Different topic: its own group (a fresh card at the top, as before).
 *
 * Vertical order: groups are anchored to when their topic FIRST appeared, so a
 * brand-new topic surfaces at the top while a new hint on an older topic stays
 * with its existing card instead of jumping to the top. Within a group, the
 * original hint is first (left) and newer ones follow (right).
 */

const DUP_THRESHOLD = 0.9; // title+summary similarity above this = same hint, drop it.
const TOPIC_TITLE_THRESHOLD = 0.34; // same-category + title similarity above this = same topic.
const TOPIC_COMBINED_THRESHOLD = 0.45;

function tokens(s: string): Set<string> {
  return new Set(
    (s ?? "")
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .split(/\s+/)
      .filter((w) => w.length > 2),
  );
}

/** Sørensen–Dice similarity over word sets (0..1). */
function dice(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  return (2 * inter) / (a.size + b.size);
}

function sim(a: string, b: string): number {
  return dice(tokens(a), tokens(b));
}

function isDuplicate(a: Hint, b: Hint): boolean {
  return sim(`${a.title} ${a.summary}`, `${b.title} ${b.summary}`) >= DUP_THRESHOLD;
}

// Competitor / third-party product names (English even in non-English calls), used
// to detect that two hints are about the SAME comparison topic — even when the
// surrounding text is Hebrew and even when a hint has no comparisonTable.
const COMPETITOR_TERMS = [
  "bedrock", "sagemaker", "redshift", "dynamodb", "aurora", "athena", "glue",
  "kinesis", "lambda", "cloudfront", "cognito", "quicksight", "fargate",
  "azure openai", "azure ml", "synapse", "cosmos", "power bi", "event hubs",
  "snowflake", "databricks", "openai",
];

/** Competitor/product terms mentioned in a hint (comparison table + title + summary). */
function competitorTerms(h: Hint): Set<string> {
  const hay = `${h.comparisonTable?.left?.name ?? ""} ${h.title} ${h.summary}`.toLowerCase();
  const out = new Set<string>();
  for (const t of COMPETITOR_TERMS) if (hay.includes(t)) out.add(t);
  return out;
}

/** Normalize a server-provided topic key for equality comparison. */
function normTopic(s: string | undefined): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isSameTopic(a: Hint, b: Hint): boolean {
  // Preferred: the model tags each hint with a canonical `topic`. Same (or highly
  // similar) topic key = one card — reliable across languages and categories.
  const na = normTopic(a.topic);
  const nb = normTopic(b.topic);
  if (na && nb) {
    if (na === nb) return true;
    if (sim(na, nb) >= 0.6) return true;
    // fall through to heuristics only when the keys clearly differ
  }

  // Competitor-aware: all "Vertex AI vs <X>" angles for the SAME competitor are one
  // topic (works across languages — product names stay English — and even without
  // a comparisonTable). A different competitor stays a separate topic.
  const ta = competitorTerms(a);
  const tb = competitorTerms(b);
  if (ta.size > 0 && tb.size > 0) {
    for (const x of ta) if (tb.has(x)) return true;
    return false;
  }

  // General: same category + light title/summary overlap.
  if (a.category !== b.category) return false;
  return (
    sim(a.title, b.title) >= TOPIC_TITLE_THRESHOLD ||
    sim(`${a.title} ${a.summary}`, `${b.title} ${b.summary}`) >= TOPIC_COMBINED_THRESHOLD
  );
}

/**
 * @param hints newest-first (as stored). Returns groups ordered for display
 * (top = most recently first-seen topic); each group ordered oldest→newest.
 */
export function groupHints(hints: Hint[]): Hint[][] {
  const rank = new Map<string, number>(); // 0 = newest
  hints.forEach((h, i) => rank.set(h.id, i));

  const groups: Hint[][] = [];
  for (const h of hints) {
    const target = groups.find((g) => g.some((x) => isSameTopic(x, h)));
    if (!target) {
      groups.push([h]);
    } else if (!target.some((x) => isDuplicate(x, h))) {
      target.push(h);
    }
    // else: near-duplicate → dropped.
  }

  const r = (h: Hint) => rank.get(h.id) ?? 0;
  // Within a group: oldest (largest rank) first so the original hint is on the left.
  for (const g of groups) g.sort((a, b) => r(b) - r(a));
  // Across groups: anchor by the oldest member; smaller max-rank = more recent
  // first-appearance = higher up.
  const anchor = (g: Hint[]) => Math.max(...g.map(r));
  groups.sort((a, b) => anchor(a) - anchor(b));
  return groups;
}
